import { useState } from 'react';
import { Menu, X, ChevronDown, Globe, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const { t, i18n } = useTranslation();

  const languages = [
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Arama:', searchQuery);
  };

  const serviceCategories = [
    {
      title: 'Belgelendirme Hizmetleri',
      items: [
        { name: 'Sürdürülebilir Turizm', link: '/services/belgelendirme' },
        { name: 'GSTC Sertifikası', link: '/services/belgelendirme' },
        { name: 'Personel Belgelendirme', link: '/services/belgelendirme' },
        { name: 'Vegan Belgelendirme', link: '/services/belgelendirme' }
      ]
    },
    {
      title: 'Yönetim Sistemleri Belgelendirme',
      items: [
        { name: 'ISO 9001 Kalite Yönetim Sistemi', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 14001 Çevre Yönetim Sistemi', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 45001 İş Sağlığı ve Güvenliği', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 50001 Enerji Yönetim Sistemi', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 10002 Müşteri Memnuniyeti', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 27001 Bilgi Güvenliği', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 27701 Kişisel Verilerin Güvenliği', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 31000 Risk Yönetim Sistemi', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 39001 Karayolu Trafik Güvenliği', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 28001 Tedarik Zinciri Yönetimi', link: '/services/yonetim-sistemleri' },
        { name: 'ISO 22301 İş Sürekliliği Yönetimi', link: '/services/yonetim-sistemleri' }
      ]
    },
    {
      title: 'Teknik Kontroller',
      items: [
        { name: 'Asansör Periyodik Kontrolü', link: '/services/teknik-kontroller' },
        { name: 'Yürüyen Merdiven Periyodik Kontrolü', link: '/services/teknik-kontroller' },
        { name: 'Fotovoltaik', link: '/services/teknik-kontroller' },
        { name: 'Memnuniyet Anketi', link: '/services/teknik-kontroller' },
        { name: 'İş Ekipmanları', link: '/services/teknik-kontroller' },
        { name: 'Basınçlı Kaplar ve Kazanlar Kontrolleri', link: '/services/teknik-kontroller' },
        { name: 'Yangından Korunma Sistemi Kontrolleri', link: '/services/teknik-kontroller' },
        { name: 'Kaldırma ve İletme Makineleri Kontrolleri', link: '/services/teknik-kontroller' },
        { name: 'Elektrik Tesisatları Kontrolleri', link: '/services/teknik-kontroller' },
        { name: 'İş Makineleri', link: '/services/teknik-kontroller' },
        { name: 'Tezgahlar', link: '/services/teknik-kontroller' }
      ]
    },
    {
      title: 'Denetim ve Gözetim Hizmetleri',
      items: [
        { name: 'Yükleme Öncesi Gözetimler', link: '/services/denetim-gozetim' },
        { name: 'İkinci Taraf Denetim', link: '/services/denetim-gozetim' },
        { name: 'Hijyen ve Sanitasyon', link: '/services/denetim-gozetim' }
      ]
    },
    {
      title: 'Enerji Verimliliği',
      items: [
        { name: 'Enerji Kimlik Belgesi', link: '/services/enerji-verimliligi' },
        { name: 'Enerji Etüdü', link: '/services/enerji-verimliligi' },
        { name: 'Enerji Verimliliği Danışmanlığı', link: '/services/enerji-verimliligi' },
        { name: 'Sıfır Atık Süreç Yönetimi', link: '/services/enerji-verimliligi' }
      ]
    },
    {
      title: 'Özel Denetimler',
      items: [
        { name: 'Okul Güvenliği', link: '/services/ozel-denetimler' },
        { name: 'Yeşil Bina', link: '/services/ozel-denetimler' },
        { name: 'Otel Denetimleri', link: '/services/ozel-denetimler' },
        { name: 'Spor Güvenliği', link: '/services/ozel-denetimler' }
      ]
    }
  ];

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          {/* Logo - Sol taraf */}
          <div className="flex items-center">
            <a href="/">
              <img 
                src="https://static.readdy.ai/image/aaba9897d2facfbc7f1204630a4017ac/3143f84cced38ce3ece6675b932547d0.png" 
                alt="IQCERT Logo" 
                className="h-20 w-auto"
              />
            </a>
          </div>

          {/* Center Navigation - Orta */}
          <nav className="hidden lg:flex items-center justify-center">
            <div className="flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium text-lg">
                {t('nav.home')}
              </a>
              <a href="/about" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium text-lg">
                {t('nav.about')}
              </a>
              
              {/* Services Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  onMouseEnter={() => setIsServicesOpen(true)}
                  className="flex items-center space-x-1 text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium text-lg"
                >
                  <span>{t('nav.services')}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {isServicesOpen && (
                  <div 
                    className="absolute top-full left-0 mt-2 w-screen max-w-6xl bg-white rounded-lg shadow-xl border z-50"
                    onMouseLeave={() => setIsServicesOpen(false)}
                    style={{ left: '50%', transform: 'translateX(-50%)' }}
                  >
                    <div className="grid grid-cols-3 gap-6 p-8">
                      {serviceCategories.map((category, index) => (
                        <div key={index} className="space-y-3">
                          <h3 className="font-semibold text-red-600 text-sm uppercase tracking-wide border-b border-gray-200 pb-2">
                            {category.title}
                          </h3>
                          <ul className="space-y-2">
                            {category.items.map((item, itemIndex) => (
                              <li key={itemIndex}>
                                <a 
                                  href={item.link} 
                                  className="text-gray-600 hover:text-red-600 transition-colors cursor-pointer text-sm block py-1"
                                >
                                  {item.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <a href="/blog" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium text-lg">
                {t('nav.blog')}
              </a>
              <a href="/contact" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium text-lg">
                {t('nav.contact')}
              </a>
            </div>
          </nav>

          {/* Right Side - Sağ taraf */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder={t('header.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm w-64"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </form>

            {/* Language Selector */}
            <div className="relative">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all cursor-pointer"
              >
                <Globe className="w-5 h-5" />
                <span className="text-sm font-medium">{currentLanguage.flag}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {isLanguageOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-2 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 cursor-pointer ${
                        i18n.language === lang.code ? 'bg-red-50 text-red-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-lg">{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-red-600 cursor-pointer"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t">
            {/* Mobile Search */}
            <div className="mb-4">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder={t('header.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              </form>
            </div>

            <nav className="flex flex-col space-y-4">
              <a href="/" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium">
                {t('nav.home')}
              </a>
              <a href="/about" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium">
                {t('nav.about')}
              </a>
              
              {/* Mobile Services */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="flex items-center justify-between w-full text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium"
                >
                  <span>{t('nav.services')}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isServicesOpen && (
                  <div className="mt-2 ml-4 space-y-2">
                    {serviceCategories.map((category, index) => (
                      <div key={index} className="space-y-1">
                        <h4 className="font-medium text-red-600 text-sm">{category.title}</h4>
                        {category.items.slice(0, 3).map((item, itemIndex) => (
                          <a 
                            key={itemIndex}
                            href={item.link} 
                            className="block text-gray-600 hover:text-red-600 transition-colors cursor-pointer text-sm ml-2"
                          >
                            {item.name}
                          </a>
                        ))}
                        {category.items.length > 3 && (
                          <span className="text-gray-400 text-xs ml-2">+{category.items.length - 3} daha fazla</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <a href="/blog" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium">
                {t('nav.blog')}
              </a>
              <a href="/contact" className="text-gray-700 hover:text-red-600 transition-colors cursor-pointer font-medium">
                {t('nav.contact')}
              </a>
              
              {/* Mobile Language Selector */}
              <div className="pt-2 border-t">
                <p className="text-sm text-gray-500 mb-2">{t('nav.language')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-2 rounded-lg text-left hover:bg-gray-50 flex items-center space-x-2 cursor-pointer ${
                        i18n.language === lang.code ? 'bg-red-50 text-red-600' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
